import { useMemo } from "react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { buildBuckets, RangeInfo, UsageSummary } from "./usageTypes";

// "__all__" charts every counted action except sign-ins.
export const ALL_ACTIONS = "__all__";

interface Props {
  summary: UsageSummary;
  range: RangeInfo;
  metricKey: string;
  metricLabel: string;
  /** Keys that make up "all actions". */
  allKeys: string[];
}

function tick(bucket: string, granularity: RangeInfo["granularity"]) {
  const d = new Date(`${bucket}T00:00:00Z`);
  return granularity === "month"
    ? d.toLocaleDateString("en-US", { month: "short", year: "2-digit", timeZone: "UTC" })
    : d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

export default function TrendChart({ summary, range, metricKey, metricLabel, allKeys }: Props) {
  const data = useMemo(() => {
    const byBucket = new Map(summary.series.map((r) => [String(r.bucket), r]));
    return buildBuckets(range.from, range.to, range.granularity).map((b) => {
      const row = byBucket.get(b);
      const value = !row
        ? 0
        : metricKey === ALL_ACTIONS
          ? allKeys.reduce((a, k) => a + (Number(row[k]) || 0), 0)
          : Number(row[metricKey]) || 0;
      return { bucket: b, label: tick(b, range.granularity), value };
    });
  }, [summary, range, metricKey, allKeys]);

  const total = data.reduce((a, d) => a + d.value, 0);

  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <p className="m-0 text-sm font-semibold text-base-content">{metricKey === ALL_ACTIONS ? "All actions" : metricLabel}</p>
        <p className="m-0 text-xs text-base-content/50">
          {total.toLocaleString("en-US")} in this range, charted by {range.granularity}
        </p>
      </div>
      <div className="h-64 w-full" role="img" aria-label={`${metricLabel} over time`}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
            <defs>
              <linearGradient id="usageFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="oklch(var(--bc) / 0.12)" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: "oklch(var(--bc) / 0.6)" }}
              tickLine={false}
              axisLine={{ stroke: "oklch(var(--bc) / 0.2)" }}
              minTickGap={24}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fontSize: 11, fill: "oklch(var(--bc) / 0.6)" }}
              tickLine={false}
              axisLine={false}
              width={44}
            />
            <Tooltip
              cursor={{ stroke: "oklch(var(--bc) / 0.3)" }}
              contentStyle={{
                background: "oklch(var(--b1))",
                border: "1px solid oklch(var(--b3))",
                borderRadius: 8,
                fontSize: 12,
                color: "oklch(var(--bc))",
              }}
              formatter={(v: number) => [v.toLocaleString("en-US"), metricKey === ALL_ACTIONS ? "All actions" : metricLabel]}
              labelFormatter={(_l, payload) => {
                const b = payload?.[0]?.payload?.bucket as string | undefined;
                return b ? `${range.granularity === "week" ? "Week of " : ""}${tick(b, range.granularity)}` : "";
              }}
            />
            <Area type="linear" dataKey="value" stroke="#3b82f6" strokeWidth={2} fill="url(#usageFill)" isAnimationActive={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
